/**
 * Integrations Model for V2 Services
 */

export interface DreameIntegration {
  region: string;
  countryCode: string;
  languageCode: string;
  username: string | null;
  password?: string;
  deviceAlias: string | null;
  hasPassword: boolean;
  updatedAt: Date | null;
  lastTestedAt: Date | null;
  lastTestStatus: 'unknown' | 'success' | 'error';
  lastTestMessage: string | null;
}

export interface IntegrationsData {
  id?: string;
  dreame?: DreameIntegration;
  createdAt?: Date;
  updatedAt?: Date;
}

export class Integrations {
  public id?: string;
  public dreame?: DreameIntegration;
  public createdAt: Date;
  public updatedAt: Date;

  constructor(data?: Partial<IntegrationsData>) {
    this.id = data?.id;
    this.dreame = data?.dreame || this.getDefaultDreameIntegration();
    this.createdAt = data?.createdAt || new Date();
    this.updatedAt = data?.updatedAt || new Date();
  }

  private getDefaultDreameIntegration(): DreameIntegration {
    return {
      region: 'us',
      countryCode: 'US',
      languageCode: 'en',
      username: null,
      deviceAlias: null,
      hasPassword: false,
      updatedAt: null,
      lastTestedAt: null,
      lastTestStatus: 'unknown',
      lastTestMessage: null,
    };
  }

  public toFirestore(): Record<string, any> {
    // Save in flat structure (root level) to match existing Firebase document
    if (!this.dreame) {
      return {
        createdAt: this.createdAt,
        updatedAt: this.updatedAt,
      };
    }

    return {
      region: this.dreame.region,
      countryCode: this.dreame.countryCode,
      languageCode: this.dreame.languageCode,
      username: this.dreame.username,
      deviceAlias: this.dreame.deviceAlias,
      // Note: credentials are handled separately (encrypted)
      lastTest: {
        status: this.dreame.lastTestStatus,
        message: this.dreame.lastTestMessage,
        testedAt: this.dreame.lastTestedAt,
      },
      updatedAt: this.updatedAt,
      createdAt: this.createdAt,
    };
  }

  public static fromFirestore(data: any): Integrations {
    // Handle both nested structure (dreame: {...}) and flat structure (root level)
    const dreameData = data.dreame || data;

    // Map Firebase structure to our model
    const dreameConfig: DreameIntegration = {
      region: dreameData.region || 'us',
      countryCode: dreameData.countryCode || 'US',
      languageCode: dreameData.languageCode || 'en',
      username: dreameData.username || null,
      deviceAlias: dreameData.deviceAlias || null,
      hasPassword: Boolean(dreameData.credentials),
      updatedAt: dreameData.updatedAt?.toDate?.() || dreameData.updatedAt || null,
      lastTestedAt: dreameData.lastTest?.testedAt?.toDate?.() || dreameData.lastTest?.testedAt || null,
      lastTestStatus: dreameData.lastTest?.status || 'unknown',
      lastTestMessage: dreameData.lastTest?.message || null,
    };

    return new Integrations({
      id: data.id,
      dreame: dreameConfig,
      createdAt: data.createdAt?.toDate?.() || data.createdAt || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt || new Date(),
    });
  }

  public updateDreameIntegration(updates: Partial<DreameIntegration>): void {
    this.dreame = { ...this.dreame!, ...updates };
    this.updatedAt = new Date();
  }
}
